import React, { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import { Box, Button, Grid, makeStyles, Typography, Theme } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'

import { Video } from 'components'
import { eventReplay } from 'helpers'
import { GameFlowStepsConfig } from 'helpers/steps'
import { GameFlowSteps, IReplayItem } from 'types'

interface EventReplayProps {
  setScene: (sceneTo: GameFlowSteps) => void
 }
 
 export const EventReplay: FC<EventReplayProps> = ({ setScene }) => {
  const classes = useStyles({})
  const [replay, setReplay] = useState<IReplayItem>(eventReplay['fullShow'])
  const [renderDemo, setRenderDemo] = useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setRenderDemo(true)
    }, GameFlowStepsConfig[GameFlowSteps.Demo].animation.time)
  }, [])

  return (
    <div className={classes.root}>
      {renderDemo && (
        <Grid 
			className={classes.mainContainer}
			container 
			direction='row'
		>
          <Grid item xs={8} className={classes.demoContainer}>
            <Video  className={classes.videoContainer}
              videoSrc={replay.videoSrc}
              trackSrc={replay.trackSrc}
			  posterSrc={replay.posterSrc}
			 />
            <Box display='flex' className={classes.contentActionBox}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    setScene(GameFlowSteps.Welcome)
                  }}
                >
                  Back
                </Button>
			</Box>
          </Grid>
          <Grid
			  xs={4}
			  lg={3}
			  className={classes.endContainer}
			  container
			  direction={'column'}
		  >
            <Grid xs={6} className={classes.endContentContainer} container alignItems='flex-start' direction='row'>
              <Grid item xs={12}>
                <Typography variant='h1' className={classes.header}>
                  {replay.title}
                </Typography>

                <Typography component='p' className={classes.body} paragraph>
                  {replay.subtitle}
                </Typography>
              </Grid>
			</Grid>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: 'calc(100% - 115px)',
    width: 'calc(100% - 64px)',
    top: '115px',
    display: 'flex',
    alignItems: 'flex-start',
    position: 'absolute',
    backgroundColor: 'transparent',
    color: '#000',
    justifyContent: 'center',

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      width: '100%',
      height: '100%',
      top: 0
    }
  },
  intro: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20000,
    display: 'flex',
    background: '#000',
    justifyContent: 'center',
    flexDirection: 'column',
    '& .video-react-control-bar': {
      display: 'none !important'
    }
  },
  mainContainer: {
    maxWidth: 1530,

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      height: '100%'
    }
  },
  contentActionBox: {
    marginTop: 27,
	
	[`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      marginTop:0
    },

    '& .MuiButton-root': {
      marginRight: theme.spacing(4),
      marginTop: 0,
      marginBottom: 0
    }
  },
  endContainer: {
    padding: '2rem 4rem 6rem 0rem',
    justifyContent: 'center',
    minHeight: '100%',

    [theme.breakpoints.down('md')]: {
      paddingRight: '6rem'
    },

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      paddingTop: '4rem',
      paddingRight: '2rem'
    }
  },
  borderTop: {
    borderTop: '1px solid #DBDBDB'
  },
  endContentContainer: {
    maxWidth: '100%',
    minHeight: '50%',

    '&:nth-child(2)': {
      [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
        alignContent: 'flex-end'
      }
    },
    '& .MuiGrid-item': {
      padding: '0',

      [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
        padding: '0 0 5px'
      }
    }
  },
  header: {
    fontSize: '28px',
    lineHeight: '1.2',
    fontWeight: 700,
    [theme.breakpoints.only('md')]: {
      fontSize: '24px !important'
    },
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      fontSize: '24px !important'
    },
    [`${theme.breakpoints.down('xs')}, screen and (max-height: 540px)`]: {
      fontSize: '20px !important'
    }
  },
  body: {
    [theme.breakpoints.only('md')]: {
      fontSize: '16px !important'
    },
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      fontSize: '14px !important',
      marginBottom: 12,
      overflowY: 'scroll'
    }
  },
  subHeader: {
    fontSize: '20px',
    lineHeight: '1.2',
    fontWeight: 700,

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      fontSize: '14px'
    }
  },
  thumbnail: {
    cursor: 'pointer',
    position: 'relative',

    '& > img': {
      width: '100%'
    },

    '& > figcaption': {
      bottom: 0,
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      position: 'absolute',
      textShadow: `1px 1px 2px ${theme.palette.common.black}`
    }
  },
  demoContainer: {
    padding: '2rem 5rem 6rem 5rem',
    width: '100%',

    [theme.breakpoints.down('md')]: {
      paddingRight: '4rem',
      paddingLeft: '4rem'
    },

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      paddingTop: '4rem',
      paddingRight: '3rem',
      paddingLeft: '4rem'
    }
  },
  videoContainer: {
    width: '100%'
  },
  '@keyframes fadeInOpacity': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  },
  '@keyframes fadeOutOpacity': {
    '0%': {
      opacity: 1
    },
    '100%': {
      opacity: 0
    }
  }
}))
